// =====================================================
// DATOS DE SUELO POR LOTE - GEOINTA
// Adaptación de DatosSuelo para un polígono específico
// src/pages/Agro/Establecimiento/DatosSueloLote.js
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Button,
} from "reactstrap"
import { getDatosSueloPoligono } from "api/agro"

// Colores por nivel de aptitud/riesgo
const COLORES_APTITUD = {
  ALTA: "success",
  MEDIA: "info",
  BAJA: "warning",
  MUY_BAJA: "danger",
  NO_DISPONIBLE: "secondary",
}

const COLORES_RIESGO = {
  BAJO: "success",
  MEDIO: "warning",
  ALTO: "danger",
  NO_DISPONIBLE: "secondary",
}

const DatosSueloLote = ({ idPoligono, poligono }) => {
  const [loading, setLoading] = useState(false)
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (idPoligono) {
      loadDatos()
    }
  }, [idPoligono])

  const loadDatos = async (force = false) => {
    setLoading(true)
    setError(null)
    try {
      const data = await getDatosSueloPoligono(idPoligono, force)
      setDatos(data)
    } catch (err) {
      console.error("Error cargando datos de suelo:", err)
      setError(
        "No se pudieron obtener datos de suelo de GeoINTA para este lote",
      )
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <Spinner />
          <p className="text-muted mt-2">Consultando GeoINTA...</p>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <Alert color="warning" className="mb-0">
            <i className="mdi mdi-alert me-2" />
            {error}
            <br />
            <small>Verifique que el lote tenga coordenadas asignadas.</small>
          </Alert>
        </CardBody>
      </Card>
    )
  }

  if (!datos) {
    return (
      <Card>
        <CardBody>
          <Alert color="info" className="mb-0">
            <i className="mdi mdi-information me-2" />
            No hay datos de suelo disponibles para este lote.
          </Alert>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="text-muted">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">
            <i className="mdi mdi-image-filter-hdr me-2" />
            Análisis de Suelo - {poligono?.nombreLote || `Lote ${idPoligono}`}
          </h5>
          <div>
            <Badge color="secondary">
              <i className="mdi mdi-source-branch me-1" />
              {datos.fuenteDato || "GeoINTA"}
            </Badge>
            <Button
              size="sm"
              color="primary"
              outline
              className="ms-2"
              onClick={() => loadDatos(true)}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </div>
        </div>

        <Row>
          {/* Ubicación */}
          {(datos.provincia || datos.departamento) && (
            <Col md={12} className="mb-3">
              <h6 className="text-muted mb-2">Ubicación</h6>
              <div className="d-flex gap-3 flex-wrap">
                {datos.provincia && (
                  <div>
                    <small className="text-muted d-block">Provincia</small>
                    <strong>{datos.provincia}</strong>
                  </div>
                )}
                {datos.departamento && (
                  <div>
                    <small className="text-muted d-block">Departamento</small>
                    <strong>{datos.departamento}</strong>
                  </div>
                )}
                {datos.partidoLocalidad && (
                  <div>
                    <small className="text-muted d-block">
                      Partido/Localidad
                    </small>
                    <strong>{datos.partidoLocalidad}</strong>
                  </div>
                )}
              </div>
            </Col>
          )}

          {/* Clasificación */}
          <Col md={6}>
            <Card className="border">
              <CardBody>
                <h6 className="mb-3">
                  <i className="mdi mdi-layers-triple me-2 text-primary" />
                  Clasificación del Suelo
                </h6>
                <div className="mb-2">
                  <small className="text-muted">Clase</small>
                  <div>
                    <strong>{datos.claseSuelo || "N/D"}</strong>
                  </div>
                </div>
                {datos.serieSuelo && (
                  <div className="mb-2">
                    <small className="text-muted">Serie</small>
                    <div>{datos.serieSuelo}</div>
                  </div>
                )}
                {datos.simboloCarta && (
                  <div className="mb-2">
                    <small className="text-muted">Símbolo Carta</small>
                    <div>
                      <code>{datos.simboloCarta}</code>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Características */}
          <Col md={6}>
            <Card className="border">
              <CardBody>
                <h6 className="mb-3">
                  <i className="mdi mdi-texture me-2 text-info" />
                  Características Físicas
                </h6>
                {datos.textura && (
                  <div className="mb-2">
                    <small className="text-muted">Textura</small>
                    <div>{datos.textura}</div>
                  </div>
                )}
                {datos.drenaje && (
                  <div className="mb-2">
                    <small className="text-muted">Drenaje</small>
                    <div>{datos.drenaje}</div>
                  </div>
                )}
                {datos.pendiente && (
                  <div className="mb-2">
                    <small className="text-muted">Pendiente</small>
                    <div>{datos.pendiente}</div>
                  </div>
                )}
                {datos.profundidad && (
                  <div className="mb-2">
                    <small className="text-muted">Profundidad</small>
                    <div>{datos.profundidad} cm</div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Aptitud y Riesgos */}
          <Col md={12} className="mt-3">
            <Row>
              <Col md={4}>
                <Card className="border text-center">
                  <CardBody>
                    <h6 className="mb-3">Aptitud Agrícola</h6>
                    <Badge
                      color={
                        COLORES_APTITUD[datos.aptitudAgricola] || "secondary"
                      }
                      className="px-3 py-2"
                      style={{ fontSize: "1rem" }}
                    >
                      {datos.aptitudAgricola?.replace("_", " ") || "N/D"}
                    </Badge>
                    {datos.indicePP && (
                      <div className="mt-2">
                        <small className="text-muted">Índice PP</small>
                        <div className="h4 mb-0">{datos.indicePP}</div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="border text-center">
                  <CardBody>
                    <h6 className="mb-3">Riesgo de Erosión</h6>
                    <Badge
                      color={COLORES_RIESGO[datos.riesgoErosion] || "secondary"}
                      className="px-3 py-2"
                      style={{ fontSize: "1rem" }}
                    >
                      {datos.riesgoErosion || "N/D"}
                    </Badge>
                  </CardBody>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="border text-center">
                  <CardBody>
                    <h6 className="mb-3">Riesgo de Anegamiento</h6>
                    <Badge
                      color={
                        COLORES_RIESGO[datos.riesgoAnegamiento] || "secondary"
                      }
                      className="px-3 py-2"
                      style={{ fontSize: "1rem" }}
                    >
                      {datos.riesgoAnegamiento || "N/D"}
                    </Badge>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>

          {/* Limitaciones */}
          {datos.limitaciones && (
            <Col md={12} className="mt-3">
              <Alert color="warning">
                <h6 className="alert-heading">
                  <i className="mdi mdi-alert-circle me-2" />
                  Limitaciones
                </h6>
                <p className="mb-0">{datos.limitaciones}</p>
              </Alert>
            </Col>
          )}

          {/* Recomendaciones */}
          {datos.cultivosRecomendados && (
            <Col md={6} className="mt-3">
              <Card className="border border-success">
                <CardBody>
                  <h6 className="mb-3 text-success">
                    <i className="mdi mdi-leaf me-2" />
                    Cultivos Recomendados
                  </h6>
                  <p className="mb-0">{datos.cultivosRecomendados}</p>
                </CardBody>
              </Card>
            </Col>
          )}

          {datos.practicasManejo && (
            <Col md={6} className="mt-3">
              <Card className="border border-info">
                <CardBody>
                  <h6 className="mb-3 text-info">
                    <i className="mdi mdi-cog me-2" />
                    Prácticas de Manejo
                  </h6>
                  <p className="mb-0">{datos.practicasManejo}</p>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        <div className="mt-3 text-muted small">
          <i className="mdi mdi-information-outline me-1" />
          Datos provistos por GeoINTA (Instituto Nacional de Tecnología
          Agropecuaria)
        </div>
      </CardBody>
    </Card>
  )
}

export default DatosSueloLote
